import {DEFAULT_HEADERS} from '@redux/config'
import {deleteProperties} from '@redux/slices/utils/delete-properties'

import getUrl from '@utils/url-utils'

import {baseApi} from './base.slice'

const TENANT_ENDPOINT = 'tenants'

const BASE_TENANT_URL = new URL(
	`${process.env.GATSBY_BACKOFFICE_BASE_URL}/${TENANT_ENDPOINT}`
).toString()

export const tenantApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		getTenants: builder.query<
			GetTenantsResponse,
			{
				limit: number
				offset: number
				filter?: {property: string; value: string}
				sort?: {direction: 'asc' | 'desc'; property: string}
			}
		>({
			query: ({limit, offset, sort, filter}) => {
				const url = getUrl(BASE_TENANT_URL, {
					queryParams: new Map([
						['limit', limit.toString()],
						['offset', offset.toString()],
						['orderBy', sort ? `${sort.property}.${sort.direction}` : ''],
					]),
				})

				if (filter?.property && filter?.value) {
					url.searchParams.set(`tenant.${filter.property}`, filter?.value)
				}

				return {
					input: url.toString(),
					init: {method: 'GET'},
				}
			},
			providesTags: ['Tenants'],
		}),
		getTenant: builder.query<TenantDetail, string>({
			query: (tenantId) => {
				const url = getUrl(BASE_TENANT_URL, {
					appendPath: `${TENANT_ENDPOINT}/${tenantId}/details`,
				})

				return {
					input: url.toString(),
					init: {method: 'GET'},
				}
			},
			providesTags: ['Tenants'],

			transformResponse: ({
				identityProvider: {type: identityProviderType, ...restIdentity},
				paymentProvider: {type: paymentProviderType, ...restPayment},
				gameTypes,
				...rest
			}: TenantDetailReturnType): TenantDetail => ({
				gameTypeIds: gameTypes.map((gameType) => gameType.id),
				gameTypes,
				identityProviderType,
				paymentProviderType,
				...rest,
				...restIdentity,
				...restPayment,
			}),
		}),
		addTenant: builder.mutation<TenantDetail, Partial<TenantDetail>>({
			query: (tenant) => {
				if (tenant.paymentProviderType === 'stripe') {
					deleteProperties(tenant, [
						'HMACKey',
						'clientKey',
						'liveEndpointUrlPrefix',
					])
				} else if (tenant.paymentProviderType === 'adyen') {
					delete tenant.webhookSecretKey
				}
				// removing some properties that BE doesn't accept
				deleteProperties(tenant, [
					'id',
					'gameTypeIds',
					'isEnabled',
					'isDeleted',
					'dashboardUrl',
					'backendDomain',
				])

				const {identityProviderType, paymentProviderType, ...rest} = tenant

				const transformedTenant = {
					identityProvider: identityProviderType,
					paymentProvider: paymentProviderType,
					...rest,
				}

				return {
					input: BASE_TENANT_URL,
					init: {
						method: 'POST',
						body: JSON.stringify(transformedTenant),
						credentials: 'include',
						headers: DEFAULT_HEADERS,
					},
				}
			},
			invalidatesTags: ['Tenants', 'GameTypes'],
		}),
		updateTenant: builder.mutation<TenantDetail, Partial<TenantDetail>>({
			query: (tenant) => {
				// only using properties that BE accepts
				const {
					id: tenantId,
					gameTypes,
					backendDomain,
					name,
					isEnabled,
					isDeleted,
					gameTypeIds,
					countries,
					languages,
					dashboardUrl,
					webAppUrl,
				} = tenant

				const transformedTenant = {
					name,
					isEnabled,
					isDeleted,
					gameTypeIds,
					countries,
					languages,
					dashboardUrl,
					webAppUrl,
					gameTypes,
					backendDomain,
				}

				return {
					input: `${BASE_TENANT_URL}/${tenantId}/details`,
					init: {
						method: 'PUT',
						body: JSON.stringify(transformedTenant),
						credentials: 'include',
						headers: DEFAULT_HEADERS,
					},
				}
			},
			invalidatesTags: ['Tenants', 'GameTypes'],
		}),
		deleteTenantCache: builder.mutation<TenantDetail, string>({
			query: (tenantId) => {
				return {
					input: `${BASE_TENANT_URL}/${tenantId}/cache`,
					init: {
						method: 'DELETE',
						credentials: 'include',
						headers: DEFAULT_HEADERS,
					},
				}
			},
			invalidatesTags: ['Tenants'],
		}),

		getTenantSettings: builder.query<TenantSettings, string>({
			query: (tenantId) => {
				return {
					input: `${BASE_TENANT_URL}/${tenantId}/settings`,
					init: {method: 'GET'},
				}
			},
			providesTags: ['TenantSettings'],
		}),
		updateTenantSettings: builder.mutation<
			void,
			{tenantId: string; tenantSettings: Partial<TenantSettings>}
		>({
			query: ({tenantId, tenantSettings}) => {
				return {
					input: `${BASE_TENANT_URL}/${tenantId}/settings`,
					init: {
						method: 'PUT',
						body: JSON.stringify(tenantSettings),
						credentials: 'include',
						headers: DEFAULT_HEADERS,
					},
				}
			},
			invalidatesTags: ['TenantSettings'],
		}),
		getTenantPickupLocations: builder.query<PickupLocation[], string>({
			query: (tenantId) => {
				return {
					input: `${BASE_TENANT_URL}/${tenantId}/pickup-locations`,
					init: {
						method: 'GET',
					},
				}
			},
		}),
	}),
})

/**
 * Export hooks for usage in functional components, which are
 * auto-generated based on the defined endpoints.
 */

export const {
	useLazyGetTenantsQuery,
	useGetTenantsQuery,
	useLazyGetTenantQuery,
	useGetTenantQuery,
	useAddTenantMutation,
	useUpdateTenantMutation,
	useDeleteTenantCacheMutation,
	useLazyGetTenantSettingsQuery,
	useUpdateTenantSettingsMutation,
	useLazyGetTenantPickupLocationsQuery,
} = tenantApi
export default tenantApi.reducer
